import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { reportSummary } from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/reports")({
  component: ReportsPage,
});

function ReportsPage() {
  const [days, setDays] = useState(30);
  const fn = useServerFn(reportSummary);
  const { data, isLoading } = useQuery({
    queryKey: ["report-summary", days],
    queryFn: () => fn({ data: { days } }),
  });

  const cards = [
    { label: "Covers", value: data?.totalCovers ?? "—" },
    { label: "Bookings", value: data?.totalBookings ?? "—" },
    { label: "No-show rate", value: data ? `${(data.noShowRate * 100).toFixed(1)}%` : "—" },
    { label: "Cancelled", value: data?.cancelled ?? "—" },
    { label: "Revenue", value: data ? `€${(data.revenueCents / 100).toFixed(0)}` : "—" },
  ];

  const maxCovers = data?.byDay.reduce((m, d) => Math.max(m, d.covers), 0) ?? 1;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Reports</h1>
          <p className="mt-1 text-sm text-muted-foreground">Covers, revenue and no-show rates.</p>
        </div>
        <select value={days} onChange={(e) => setDays(+e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-border bg-card/40 p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</div>
            <div className="mt-1 font-display text-3xl">{c.value}</div>
          </div>
        ))}
      </div>

      <div>
        <h2 className="font-display text-xl">Covers per day</h2>
        <div className="mt-4 rounded-2xl border border-border bg-card/30 p-5">
          {isLoading || !data ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : data.byDay.length === 0 ? (
            <div className="text-sm text-muted-foreground">No bookings in this window.</div>
          ) : (
            <ul className="space-y-2">
              {data.byDay.map((d) => (
                <li key={d.day} className="grid grid-cols-[110px_1fr_60px] items-center gap-3 text-xs">
                  <span className="text-muted-foreground">{d.day}</span>
                  <div className="h-2 rounded-full bg-muted/30">
                    <div className="h-2 rounded-full bg-primary" style={{ width: `${(d.covers / maxCovers) * 100}%` }} />
                  </div>
                  <span className="text-right font-mono">{d.covers}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {data && data.byType.length > 0 && (
        <div>
          <h2 className="font-display text-xl">By booking type</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {data.byType.map((t) => (
              <div key={t.type} className="rounded-2xl border border-border bg-card/40 p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{t.type.replace(/_/g, " ")}</div>
                <div className="mt-1 font-display text-2xl">{t.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
